import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ConversationSummary,
  CrossTenantContacts,
  CrossTenantConversation,
} from '@up2d8/contracts';
import { Button, Card, EmptyState, Input, LoadingState, useToast } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { renderAppTabLink, useAppTabs } from '../app-tabs';
import { DeskShell } from '../desk/desk-shell';
import { MessagesIcon } from '../messages-icon';
import { NotificationBell } from '../notification-bell';

const keyOf = (person: { tenantId: string; personId: string }) =>
  `${person.tenantId}:${person.personId}`;

/**
 * Een gesprek over bedrijven heen beginnen (ACC-4).
 *
 * De lijst toont alleen wie de server vindbaar noemt: wie een bedrijf met je deelt en het recht
 * heeft, één rij per mens. Eén persoon kiezen opent het gesprek met die mens in je gewone inbox
 * (CHT-1): het bestaande, in welk bedrijf ook, of een nieuw in een bedrijf dat jullie delen. Meer
 * kiezen maakt een groep over bedrijven heen, en die heeft een naam nodig.
 */
export function CrossTenantNewPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tabs = useAppTabs();
  const [chosen, setChosen] = useState<string[]>([]);
  const [title, setTitle] = useState('');

  const contacts = useQuery({
    queryKey: ['shared-contacts'],
    retry: false,
    queryFn: async (): Promise<CrossTenantContacts | null> => {
      const { data, response } = await api.GET('/api/v1/shared-conversations/contacts');
      if (response.status === 404) return null;
      if (!data) throw new Error('Ophalen mislukt');
      return data as CrossTenantContacts;
    },
  });

  const start = useMutation({
    mutationFn: async (): Promise<{ to: 'inbox' | 'shared'; id: string }> => {
      const people = (contacts.data?.items ?? []).filter((person) =>
        chosen.includes(keyOf(person)),
      );
      if (people.length === 1) {
        const [person] = people as [(typeof people)[number]];
        const { data, response } = await api.POST('/api/v1/conversations/with', {
          body: { tenantId: person.tenantId, personId: person.personId },
        });
        if (data) return { to: 'inbox', id: (data as ConversationSummary).id };
        // Geen bedrijf waarin jullie allebei kunnen chatten: dan een gesprek over bedrijven heen,
        // zoals voorheen.
        if (response.status !== 404 && response.status !== 409) throw new Error('Starten mislukt');
      }
      const { data, error } = await api.POST('/api/v1/shared-conversations', {
        body: {
          participants: people.map((person) => ({
            tenantId: person.tenantId,
            personId: person.personId,
          })),
          title: people.length > 1 ? title.trim() : undefined,
        },
      });
      if (error || !data) throw error ?? new Error('Starten mislukt');
      return { to: 'shared', id: (data as CrossTenantConversation).id };
    },
    onSuccess: (started) => {
      if (started.to === 'inbox') {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void navigate({
          to: '/app/gesprekken/$conversationId',
          params: { conversationId: started.id },
        });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['shared-conversations'] });
      void navigate({
        to: '/app/over-bedrijven/$conversationId',
        params: { conversationId: started.id },
      });
    },
    onError: () => toast.show({ tone: 'danger', title: t('crossTenant.startFailed') }),
  });

  const items = contacts.data?.items ?? [];
  const isGroup = chosen.length > 1;
  const canStart = chosen.length > 0 && (!isGroup || title.trim().length > 0);

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="messages"
      renderLink={renderAppTabLink}
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-element">
        <button
          type="button"
          data-touch-target
          onClick={() => void navigate({ to: '/app/gesprekken' })}
          className="inline-flex min-h-touch items-center gap-1 self-start rounded-pill px-2 text-small text-text-secondary hover:text-brand-primary"
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          {t('messaging.title')}
        </button>
        <h1 className="font-heading text-page-title font-semibold text-text-primary">
          {t('crossTenant.newTitle')}
        </h1>
        <p className="text-small text-text-secondary">{t('crossTenant.newHint')}</p>

        {contacts.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : items.length === 0 ? (
          <Card>
            <EmptyState
              title={t('crossTenant.noContacts')}
              description={t('crossTenant.noContactsHint')}
            />
          </Card>
        ) : (
          <Card>
            <ul className="flex flex-col" data-testid="cross-tenant-contacts">
              {items.map((person) => {
                const key = keyOf(person);
                return (
                  <li key={key} className="border-b border-border-light last:border-b-0">
                    <label className="flex min-h-touch cursor-pointer items-center gap-element py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        data-testid="cross-tenant-contact"
                        checked={chosen.includes(key)}
                        onChange={(event) =>
                          setChosen((current) =>
                            event.target.checked
                              ? [...current, key]
                              : current.filter((item) => item !== key),
                          )
                        }
                      />
                      <span className="flex-1">
                        <span className="block text-content text-text-primary">
                          {person.displayName}
                        </span>
                        <span className="block text-caption text-text-secondary">
                          {person.tenantName}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        {isGroup && (
          <Input
            label={t('crossTenant.groupName')}
            value={title}
            maxLength={80}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}

        <Button
          onClick={() => start.mutate()}
          disabled={!canStart}
          loading={start.isPending}
          data-testid="cross-tenant-start"
        >
          {isGroup ? t('crossTenant.startGroup') : t('crossTenant.start')}
        </Button>
      </div>
    </DeskShell>
  );
}
